import { Box, Typography } from "@mui/material";
import { formatAED } from "../utils/format";

// Reusable building blocks for table cells, so every list reads the same way.

// Column props that right-align a numeric column (header and cells together).
export const numericColumn = {
  muiTableHeadCellProps: { align: "right" },
  muiTableBodyCellProps: { align: "right" },
};

// An amount, right-aligned with tabular digits. `sub` is an optional muted line
// underneath (e.g. "Advance: 40.00"); `tone` colours the main figure.
const AMOUNT_TONES = { default: "#111827", green: "#15803d", amber: "#b45309", red: "#b42318", muted: "#9ca3af" };
export function AmountCell({ value, sub, subTone = "muted", tone = "default", zeroAsDash = false }) {
  const n = Number(value) || 0;
  return (
    <Box sx={{ textAlign: "right", lineHeight: 1.3 }}>
      <Typography
        component="div"
        sx={{ fontSize: 13, fontWeight: 600, color: zeroAsDash && n === 0 ? AMOUNT_TONES.muted : AMOUNT_TONES[tone] || AMOUNT_TONES.default }}
      >
        {zeroAsDash && n === 0 ? "-" : formatAED(n)}
      </Typography>
      {sub && (
        <Typography component="div" sx={{ fontSize: 11.5, color: AMOUNT_TONES[subTone] || AMOUNT_TONES.muted }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
}

// A small rounded status pill with an optional muted detail line underneath.
const PILL_TONES = {
  green: { bg: "#ecfdf3", fg: "#027a48", dot: "#12b76a" },
  amber: { bg: "#fffaeb", fg: "#b54708", dot: "#f79009" },
  red: { bg: "#fef3f2", fg: "#b42318", dot: "#f04438" },
  blue: { bg: "#eff8ff", fg: "#175cd3", dot: "#2e90fa" },
  gray: { bg: "#f2f4f7", fg: "#475467", dot: "#98a2b3" },
};
export function StatusPill({ label, tone = "gray", detail, onClick, title }) {
  const t = PILL_TONES[tone] || PILL_TONES.gray;
  return (
    <Box
      onClick={onClick}
      title={title}
      role={onClick ? "button" : undefined}
      data-lmggt-handled={onClick ? "true" : undefined}
      sx={{ lineHeight: 1.25, cursor: onClick ? "pointer" : "default", display: "inline-block" }}
    >
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          px: 1,
          py: "2px",
          borderRadius: "999px",
          bgcolor: t.bg,
          color: t.fg,
          fontSize: 12,
          fontWeight: 600,
          whiteSpace: "nowrap",
          ...(onClick && { "&:hover": { filter: "brightness(0.96)" } }),
        }}
      >
        <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: t.dot }} />
        {label}
      </Box>
      {detail && (
        <Typography component="div" sx={{ fontSize: 11, color: "#667085", mt: "2px" }}>
          {detail}
        </Typography>
      )}
    </Box>
  );
}

// Maps the document payment-status labels used by the CDN / Proforma / Invoice lists to a pill tone.
export const paymentStatusTone = (label) =>
  ({
    Paid: "green",
    "Partially Paid": "amber",
    Unpaid: "gray",
    Overdue: "red",
    Cancelled: "gray",
    "On invoice": "blue",
  })[label] || "gray";
